import { supabase } from '../lib/supabase';

// TypeScript Interfaces
export interface SubscriptionPlan {
  id: string;
  name: string;
  plan_type: 'free' | 'paid';
  price_vatu: number;
  duration_days: number;
  is_active: boolean;
  features: string[];
}

export interface UserSubscription {
  id: string;
  user_id: string;
  plan_id: string;
  start_date: string;
  end_date: string;
  access_status: 'active' | 'locked' | 'grace_period';
  payment_due_date: string | null;
  grace_period_end: string | null;
  android_sync_date: string | null;
  locked_at: string | null;
  plan?: SubscriptionPlan;
}

export interface PaymentRecord {
  id: string;
  subscription_id: string;
  user_id: string;
  amount_vatu: number;
  payment_method: string;
  payment_reference: string | null;
  payment_proof_url: string | null;
  payment_status: 'pending' | 'approved' | 'rejected';
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  payment_date: string;
}

export interface AccessControlLog {
  id: string;
  user_id: string;
  subscription_id: string;
  action: string;
  previous_status: string | null;
  new_status: string | null;
  reason: string | null;
  created_by: string | null;
  created_at: string;
}

// Subscription Plans
export const getSubscriptionPlans = async (): Promise<SubscriptionPlan[]> => {
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .eq('is_active', true)
    .order('price_vatu', { ascending: true });

  if (error) throw error;
  
  // Map database fields to interface
  return (data || []).map((plan: any) => ({
    id: plan.id,
    name: plan.plan_name || plan.name || 'Unnamed Plan',
    plan_type: plan.plan_type || 'free',
    price_vatu: plan.price_vatu || 0,
    duration_days: plan.duration_days || 30,
    is_active: plan.is_active ?? true,
    features: Array.isArray(plan.features) 
      ? plan.features 
      : (typeof plan.features === 'object' && plan.features !== null 
          ? Object.values(plan.features) 
          : [])
  }));
};

// User Subscriptions
export const getUserSubscription = async (userId: string): Promise<UserSubscription | null> => {
  const { data, error } = await supabase
    .from('user_subscriptions')
    .select(`
      *,
      plan:subscription_plans(*)
    `)
    .eq('user_id', userId)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
};

export const getAllSubscriptions = async (): Promise<UserSubscription[]> => {
  const { data, error } = await supabase
    .from('user_subscriptions')
    .select(`
      *,
      plan:subscription_plans(*),
      user:user_profiles(email, full_name)
    `)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const createFreeSubscription = async (
  userId: string
): Promise<UserSubscription> => {
  // Get free plan
  const { data: freePlan, error: planError } = await supabase
    .from('subscription_plans')
    .select('id, duration_days')
    .eq('plan_type', 'free')
    .eq('is_active', true)
    .single();

  if (planError) throw planError;

  const startDate = new Date();
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + (freePlan.duration_days || 30));

  const { data, error } = await supabase
    .from('user_subscriptions')
    .insert({
      user_id: userId,
      plan_id: freePlan.id,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      payment_due_date: null, // Free plan has no payment due date
      subscription_status: 'active',
      access_status: 'active',
      access_locked: false
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const createSubscription = async (
  userId: string,
  planId: string
): Promise<UserSubscription> => {
  // Get plan details including plan_type
  const { data: plan, error: planError } = await supabase
    .from('subscription_plans')
    .select('duration_days, plan_type')
    .eq('id', planId)
    .single();

  if (planError) throw planError;

  const startDate = new Date();
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + plan.duration_days);

  // For free plans, set subscription_status to 'active' immediately
  // For paid plans, set to 'pending' (will be activated after payment approval)
  const subscriptionStatus = plan.plan_type === 'free' ? 'active' : 'pending';

  const { data, error } = await supabase
    .from('user_subscriptions')
    .insert({
      user_id: userId,
      plan_id: planId,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      payment_due_date: endDate.toISOString(),
      subscription_status: subscriptionStatus,
      access_status: 'active'
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateSubscriptionAccess = async (
  subscriptionId: string,
  accessStatus: 'active' | 'locked' | 'grace_period',
  reason?: string
): Promise<void> => {
  const { error } = await supabase
    .from('user_subscriptions')
    .update({ 
      access_status: accessStatus,
      locked_at: accessStatus === 'locked' ? new Date().toISOString() : null,
      updated_at: new Date().toISOString()
    })
    .eq('id', subscriptionId);

  if (error) throw error;

  // Log the action
  const { data: subscription } = await supabase
    .from('user_subscriptions')
    .select('user_id')
    .eq('id', subscriptionId)
    .single();

  if (subscription) {
    await logAccessControl(
      subscription.user_id,
      subscriptionId,
      'status_update',
      null,
      accessStatus,
      reason
    );
  }
};

export const recordAndroidSync = async (subscriptionId: string): Promise<void> => {
  const { error } = await supabase
    .from('user_subscriptions')
    .update({ 
      android_sync_date: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .eq('id', subscriptionId);

  if (error) throw error;
};

// Payment Records
export const createPaymentRecord = async (
  subscriptionId: string,
  userId: string,
  amountVatu: number,
  paymentReference?: string,
  paymentProofUrl?: string
): Promise<PaymentRecord> => {
  const { data, error } = await supabase
    .from('payment_records')
    .insert({
      subscription_id: subscriptionId,
      user_id: userId,
      amount_vatu: amountVatu,
      payment_reference: paymentReference,
      payment_proof_url: paymentProofUrl,
      payment_status: 'pending'
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getPaymentRecords = async (userId?: string): Promise<PaymentRecord[]> => {
  let query = supabase
    .from('payment_records')
    .select(`
      *,
      user:user_profiles(email, full_name),
      subscription:user_subscriptions(*)
    `)
    .order('created_at', { ascending: false });

  if (userId) {
    query = query.eq('user_id', userId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const approvePayment = async (
  paymentId: string,
  approvedBy: string
): Promise<void> => {
  const { data: payment, error: fetchError } = await supabase
    .from('payment_records')
    .select('subscription_id, user_id, amount_vatu')
    .eq('id', paymentId)
    .single();

  if (fetchError) throw fetchError;

  // Approve payment
  const { error: updateError } = await supabase
    .from('payment_records')
    .update({
      payment_status: 'approved',
      approved_by: approvedBy,
      approved_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .eq('id', paymentId);

  if (updateError) throw updateError;

  // Extend subscription
  const { data: subscription, error: subError } = await supabase
    .from('user_subscriptions')
    .select('end_date, plan:subscription_plans(duration_days)')
    .eq('id', payment.subscription_id)
    .single();

  if (subError) throw subError;

  const currentEndDate = new Date(subscription.end_date);
  const newEndDate = new Date(currentEndDate);
  newEndDate.setDate(newEndDate.getDate() + subscription.plan.duration_days);

  await supabase
    .from('user_subscriptions')
    .update({
      end_date: newEndDate.toISOString(),
      payment_due_date: newEndDate.toISOString(),
      access_status: 'active',
      locked_at: null,
      updated_at: new Date().toISOString()
    })
    .eq('id', payment.subscription_id);

  // Log the action
  await logAccessControl(
    payment.user_id,
    payment.subscription_id,
    'payment_approved',
    null,
    'active',
    `Payment of ${payment.amount_vatu} VT approved`
  );
};

export const rejectPayment = async (
  paymentId: string,
  rejectedBy: string,
  reason: string
): Promise<void> => {
  const { error } = await supabase
    .from('payment_records')
    .update({
      payment_status: 'rejected',
      approved_by: rejectedBy,
      rejection_reason: reason,
      updated_at: new Date().toISOString()
    })
    .eq('id', paymentId);

  if (error) throw error;
};

// Access Control Logs
export const getAccessControlLogs = async (userId?: string): Promise<AccessControlLog[]> => {
  let query = supabase
    .from('access_control_logs')
    .select(`
      *,
      user:user_profiles(email, full_name),
      created_by_user:user_profiles!access_control_logs_created_by_fkey(email, full_name)
    `)
    .order('created_at', { ascending: false });

  if (userId) {
    query = query.eq('user_id', userId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

const logAccessControl = async (
  userId: string,
  subscriptionId: string,
  action: string,
  previousStatus: string | null,
  newStatus: string | null,
  reason?: string
): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  await supabase
    .from('access_control_logs')
    .insert({
      user_id: userId,
      subscription_id: subscriptionId,
      action,
      previous_status: previousStatus,
      new_status: newStatus,
      reason,
      created_by: user?.id
    });
};

// Notifications
export const getUserNotifications = async (userId: string): Promise<any[]> => {
  const { data, error } = await supabase
    .from('notification_queue')
    .select('*')
    .eq('user_id', userId)
    .eq('sent', false)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const markNotificationAsSent = async (notificationId: string): Promise<void> => {
  await supabase
    .from('notification_queue')
    .update({ 
      sent: true, 
      sent_at: new Date().toISOString() 
    })
    .eq('id', notificationId);
};